import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useResizeObserver } from "client/Hooks";
import styles from "./TagList.module.less";
import {
	Tag,
	TagColor,
	TagSize
} from "../Tag/Tag";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import { TagModel } from "../TagSelect/TagSelect";
import { TagListDialog } from "../TagListDialog/TagListDialog";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { calculateAmountOfListItemThatFitInTheContainer } from "client/Misc/domUtils";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { never } from "client_legacy/ndaw/errors";

export type TagListResponsiveBehavior = "wrap" | "more-in-dialog";

type TagListAlignment = "left" | "right" | "center";

export type TagListProps<TValue> = {
	tags: readonly TagModel<TValue>[];
	dialogTitle?: string;
	onTagClick?(tag: TValue | null): void;
	onTagDelete?(index: number): void;
	alignment?: TagListAlignment;
	trackingId?: string;
	responsiveBehavior?: TagListResponsiveBehavior;
	areTagsAlreadyPreordered?: boolean;
	shouldBubbleTagClick?: boolean;
	showMoreTagsColor?: TagColor;
	size?: TagSize;
};

export const TagList = <TValue, >(props: TagListProps<TValue>): ReactElement => {
	const {
		tags,
		dialogTitle = localize("_Tags"),
		onTagClick,
		onTagDelete,
		alignment = "left",
		responsiveBehavior = "more-in-dialog",
		areTagsAlreadyPreordered = false,
		trackingId,
		shouldBubbleTagClick,
		showMoreTagsColor = "primary",
		size
	} = props;

	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const hiddenElementCounterRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	const { width } = useResizeObserver(listRef);

	const [hiddenTagsNumber, setHiddenTagsNumber] = useState<number>(0);
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	const sortedTags = useMemo(
		(): readonly TagModel<TValue>[] => {
			return areTagsAlreadyPreordered
				? tags
				: [...tags]
					.sort(
						(a, b) => nativeSortFunction(
							a.text,
							b.text,
							DataSourceSortDirection.Ascending
						)
					);
		},
		[areTagsAlreadyPreordered, tags]
	);

	const getValueTrackingId = useCallback(
		(item: TagModel<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	useEffect(
		(): void => {
			switch (responsiveBehavior) {
				case "wrap":
					setHiddenTagsNumber(0);
					break;
				case "more-in-dialog":
					if (isNotNil(listRef.current)) {
						const numberOfVisibleItems: number = calculateAmountOfListItemThatFitInTheContainer(
							listRef.current,
							hiddenElementCounterRef.current,
							width,
							sortedTags.length
						);

						setHiddenTagsNumber(
							sortedTags.length - numberOfVisibleItems
						);
					}
					break;
				default:
					never(responsiveBehavior);
			}
		},
		[
			sortedTags,
			width,
			hiddenTagsNumber,
			responsiveBehavior
		]
	);

	const openDialogHandler = useCallback(
		(): void => setIsDialogOpen(true),
		[]
	);

	return (
		<>
			<ul
				{...setTrackingId(trackingId)}
				ref={listRef}
				className={classNames(
					styles.root,
					alignmentToStyles[alignment],
					{
						[styles.root_wrap]: responsiveBehavior === "wrap"
					}
				)}
			>
				{sortedTags.map(((tag, index) => {
					const isHidden: boolean = index > sortedTags.length - hiddenTagsNumber - 1;

					return (
						<li
							key={index}
							className={classNames(
								styles.listItem,
								{
									[styles.listItem_hidden]: isHidden
								}
							)}
						>
							<Tag
								text={tag.text}
								onClick={
									onTagClick
										? () => onTagClick(tag.value)
										: undefined
								}
								onDelete={
									onTagDelete
										? () => onTagDelete(index)
										: undefined
								}
								color={tag.color}
								trackingId={generateTrackingId(
									trackingId,`|${getValueTrackingId(tag)}__${isHidden ? "hidden" : "visible"}`
								)}
								isDisabled={tag.isDisabled}
								shouldBubbleClick={shouldBubbleTagClick}
								size={size}
								highlight={tag.highlight}
							/>
						</li>
					);
				}))}

				<li
					ref={hiddenElementCounterRef}
					className={classNames(
						styles.listItem,
						{
							[styles.listItem_hidden]: hiddenTagsNumber === 0
						}
					)}
				>
					<Tag
						text={"+ " + hiddenTagsNumber}
						onClick={openDialogHandler}
						trackingId={generateTrackingId(trackingId, "__more-button")}
						shouldBubbleClick={shouldBubbleTagClick}
						color={showMoreTagsColor}
						size={size}
					/>
				</li>
			</ul>
			{isDialogOpen && (
				<TagListDialog
					title={dialogTitle}
					onClose={() => setIsDialogOpen(false)}
					tags={sortedTags}
					onTagClick={onTagClick}
					trackingId={generateTrackingId(trackingId, "__dialog")}
				/>
			)}
		</>
	);
};

TagList.displayName = "TagList";

const alignmentToStyles: {[key in TagListAlignment]: string | null} = {
	"left": null,
	"right": styles.root_right,
	"center": styles.root_center,
};
